/**
 * TaskBoard — premium card design with large donut chart, color accent,
 * checkbox items, and completed/incomplete counters.
 * Max 10 items per board.
 */

import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Trash2, Check, MoreHorizontal, Pencil, X } from 'lucide-react'

const MAX_ITEMS_PER_BOARD = 10

/** Unified color for all boards */
const BOARD_COLOR = { accent: '#0061FF', glow: 'rgba(0,97,255,0.08)' }

interface TaskBoardItem {
  id: string
  text: string
  completed: boolean
  sort_order: number
}

interface TaskBoardProps {
  boardId: string
  boardName: string
  items: TaskBoardItem[]
  isDefault: boolean
  colorIndex: number
  onToggleItem: (itemId: string, completed: boolean) => void
  onAddItem: (boardId: string, text: string, sortOrder: number) => void
  onClearCompleted: (boardId: string) => void
  onDeleteItem: (itemId: string) => void
  onDeleteBoard: (boardId: string) => void
  onRenameBoard: (boardId: string, name: string) => void
}

export const TaskBoard: React.FC<TaskBoardProps> = React.memo(({
  boardId,
  boardName,
  items,
  isDefault,
  colorIndex: _colorIndex,
  onToggleItem,
  onAddItem,
  onClearCompleted,
  onDeleteItem,
  onDeleteBoard,
  onRenameBoard,
}) => {
  const { t } = useTranslation()
  const [newText, setNewText] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(boardName)

  const color = BOARD_COLOR
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order)
  const completedCount = sorted.filter((i) => i.completed).length
  const totalCount = sorted.length
  const incompleteCount = totalCount - completedCount
  const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0

  const handleAdd = useCallback((): void => {
    const trimmed = newText.trim()
    if (!trimmed || totalCount >= MAX_ITEMS_PER_BOARD) return
    const maxSort = items.length > 0 ? Math.max(...items.map((i) => i.sort_order)) + 1 : 0
    onAddItem(boardId, trimmed, maxSort)
    setNewText('')
  }, [newText, totalCount, boardId, onAddItem, items])

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') handleAdd()
  }

  const handleRename = (): void => {
    const trimmed = editName.trim()
    if (trimmed && trimmed !== boardName) {
      onRenameBoard(boardId, trimmed)
    }
    setIsEditing(false)
  }

  // Donut chart dimensions
  const donutSize = 72
  const strokeW = 5
  const radius = (donutSize - strokeW) / 2
  const circumference = 2 * Math.PI * radius
  const filled = totalCount > 0 ? (completedCount / totalCount) * circumference : 0

  return (
    <div
      className="rounded-2xl border border-white/[0.06] overflow-hidden flex flex-col bg-white/[0.03]"
    >
      {/* Top section: donut + name + menu */}
      <div className="flex flex-col items-center pt-5 pb-3 px-4 gap-2 relative">
        {/* Menu button */}
        <div className="absolute top-3 right-3">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1.5 rounded-lg text-text-hint hover:text-text hover:bg-white/5 transition-colors tap-highlight-transparent"
          >
            <MoreHorizontal size={16} />
          </button>

          <AnimatePresence>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-8 z-50 bg-surface border border-border rounded-xl shadow-lg overflow-hidden min-w-[160px]"
                >
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      setEditName(boardName)
                      setIsEditing(true)
                    }}
                    className="flex items-center gap-3 w-full px-4 py-3 text-left text-[14px] text-text hover:bg-white/5 transition-colors"
                  >
                    <Pencil size={14} className="text-text-hint" />
                    {t('common.edit')}
                  </button>
                  {!isDefault && (
                    <button
                      onClick={() => {
                        setMenuOpen(false)
                        onDeleteBoard(boardId)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-left text-[14px] text-danger hover:bg-danger/5 transition-colors"
                    >
                      <Trash2 size={14} />
                      {t('common.delete')}
                    </button>
                  )}
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* Donut chart */}
        <svg
          width={donutSize}
          height={donutSize}
          viewBox={`0 0 ${donutSize} ${donutSize}`}
          className="shrink-0"
        >
          {/* Track */}
          <circle
            cx={donutSize / 2}
            cy={donutSize / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeW}
            className="text-white/[0.08]"
          />
          {/* Filled arc */}
          {totalCount > 0 && (
            <motion.circle
              cx={donutSize / 2}
              cy={donutSize / 2}
              r={radius}
              fill="none"
              stroke={color.accent}
              strokeWidth={strokeW}
              strokeDasharray={`${filled} ${circumference - filled}`}
              strokeDashoffset={circumference / 4}
              strokeLinecap="round"
              initial={{ strokeDasharray: `0 ${circumference}` }}
              animate={{ strokeDasharray: `${filled} ${circumference - filled}` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            />
          )}
          {/* Center % */}
          <text
            x={donutSize / 2}
            y={donutSize / 2}
            textAnchor="middle"
            dominantBaseline="central"
            className="fill-text font-display font-bold"
            style={{ fontSize: '16px' }}
          >
            {percent}%
          </text>
        </svg>

        {/* Board name */}
        {isEditing ? (
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename() }}
            onBlur={handleRename}
            autoFocus
            className="w-full bg-transparent text-center text-[15px] font-display font-bold text-text outline-none border-b border-primary/50"
          />
        ) : (
          <h3
            className="font-display font-bold text-text text-[15px] truncate max-w-full text-center"
            style={{ color: color.accent }}
          >
            {boardName}
          </h3>
        )}
      </div>

      {/* Task items with colored accent bar on left */}
      <div className="flex-1 px-3 pb-2">
        <AnimatePresence initial={false}>
          {sorted.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="group"
            >
              <div
                className="flex items-center gap-2.5 py-[7px] px-2.5 my-[2px] rounded-lg transition-colors"
                style={{
                  background: item.completed ? 'transparent' : color.glow,
                  borderLeft: `3px solid ${item.completed ? 'transparent' : color.accent}`,
                }}
              >
                <button
                  onClick={() => onToggleItem(item.id, !item.completed)}
                  className="w-[20px] h-[20px] rounded flex items-center justify-center shrink-0 transition-all border"
                  style={{
                    borderColor: item.completed ? color.accent : 'rgba(255,255,255,0.15)',
                    backgroundColor: item.completed ? `${color.accent}20` : 'transparent',
                  }}
                >
                  {item.completed && <Check size={12} style={{ color: color.accent }} />}
                </button>
                <span
                  className={`flex-1 text-[13px] leading-snug transition-all truncate ${
                    item.completed ? 'text-text-hint/60 line-through' : 'text-text'
                  }`}
                >
                  {item.text}
                </span>
                <button
                  onClick={() => onDeleteItem(item.id)}
                  className="p-1 opacity-40 lg:opacity-0 lg:group-hover:opacity-100 text-text-hint hover:text-danger transition-all tap-highlight-transparent shrink-0"
                >
                  <X size={12} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Add new item */}
        {totalCount < MAX_ITEMS_PER_BOARD && (
          <div className="flex items-center gap-2.5 py-[7px] px-2.5 my-[2px]">
            <div className="w-[20px] h-[20px] flex items-center justify-center shrink-0">
              <Plus size={13} style={{ color: color.accent }} />
            </div>
            <input
              type="text"
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('home.addTask')}
              className="flex-1 bg-transparent text-[13px] text-text placeholder:text-text-hint/40 outline-none"
              maxLength={200}
            />
          </div>
        )}
      </div>

      {/* Footer: counters + clear button */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.04] text-[12px]"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold" style={{ color: color.accent }}>{completedCount}</span>
            <span className="text-text-hint">{t('home.completed')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-text-hint">{incompleteCount}</span>
            <span className="text-text-hint">{t('home.incomplete')}</span>
          </div>
        </div>
        {completedCount > 0 && (
          <button
            onClick={() => onClearCompleted(boardId)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-text-hint hover:text-danger hover:bg-danger/10 transition-colors tap-highlight-transparent"
          >
            <Trash2 size={11} />
            <span>{t('home.clearCompleted')}</span>
          </button>
        )}
      </div>
    </div>
  )
})
TaskBoard.displayName = 'TaskBoard'
